import { Button, DatePicker, Form, FormProps, Icons, Input, Select, Space, useSelect } from "@pankod/refine-antd";
import { useMemo } from "react";
import { arrayExtensions, stringExtensions } from "lib-common";
import { DATAPROVIDER_LOOKUP, RESOURCE_PATH } from "scripts/site";
import { ILookup, ILocations, ISawAssetMetadata } from "interfaces";

export const SawAssetFilter: React.FC<{
  formProps: FormProps,
  onApplyFilter: () => void,
  metadata?: ISawAssetMetadata
}> = ({ formProps, onApplyFilter, metadata }) => {
  const { selectProps: userProps } = useSelect<ILookup>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.PEOPLE,
    optionLabel: "nameEmail",
    optionValue: "id",
    sort: [{ field: "lastAccessedAt", order: "desc" }],
    fetchSize: 100,
  });

  const { selectProps: locationProps } = useSelect<ILocations>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.LOCATION,
    optionLabel: "name",
    optionValue: "id",
    sort: [{ field: "name", order: "asc" }],
    fetchSize: 100,
  });

  const stateOptions = useMemo(() => {
    const sortedStates = arrayExtensions.sortBy("order", metadata?.states ?? []);
    return (sortedStates ?? []).map((state, index) => ({
      key: state.state ?? String(index),
      value: state.state,
      label: state.displayName || (state.state ? stringExtensions.capitalize(state.state) : state.state),
    }));
  }, [metadata?.states]);

  const makeModelOptions = useMemo(() => {
    return (metadata?.config?.makeModel ?? []).map((value) => ({
      value,
      label: value,
    }));
  }, [metadata?.config?.makeModel]);

  const handleClear = () => {
    formProps.form?.resetFields();
  };

  return (
    <Form layout="vertical" {...formProps} autoComplete="off">
      <Form.Item label="Search" name="q">
        <Input
          placeholder={metadata?.config?.search?.textMessages?.searchTextPlaceholder || "Search SAW Asset"}
          prefix={<Icons.SearchOutlined />}
        />
      </Form.Item>
      <Form.Item label="SAW Asset ID" name="sawAssetId">
        <Input placeholder="SAW Asset ID" allowClear />
      </Form.Item>
      <Form.Item label="Serial Number" name="serialNumber">
        <Input placeholder="Serial Number" allowClear />
      </Form.Item>
      <Form.Item label="Allocated To" name="assignedTo">
        <Select allowClear showSearch placeholder="Search Name or Email" {...userProps} />
      </Form.Item>
      <Form.Item label="State" name="state">
        <Select allowClear mode="multiple" placeholder="State">
          {stateOptions.map((option) => (
            <Select.Option value={option.value} key={option.key}>{option.label}</Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item label="Date of Delivery" name="dateOfDelivery">
        <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" allowClear />
      </Form.Item>
      <Form.Item label="Dock Serial Number" name="dockSerialNumber">
        <Input placeholder="Dock Serial Number" allowClear />
      </Form.Item>
      <Form.Item label="Employee ID" name="tcsEmployeeId">
        <Input placeholder="Employee ID" allowClear />
      </Form.Item>
      <Form.Item label="Make/Model" name="makeModel">
        {makeModelOptions.length ? (
          <Select allowClear mode="multiple" placeholder="Select Make/Model" options={makeModelOptions} />
        ) : (
          <Input placeholder="Make/Model" allowClear />
        )}
      </Form.Item>
      <Form.Item label="Associate Location" name="associateLocation">
        <Select allowClear showSearch placeholder="Select Location" {...locationProps} />
      </Form.Item>
      <Form.Item>
        <Space>
          <Button htmlType="submit" type="primary" icon={<Icons.FilterOutlined />} onClick={onApplyFilter}>
            Apply
          </Button>
          <Button icon={<Icons.ClearOutlined />} htmlType="submit" onClick={handleClear}>
            Clear
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
